// src/components/blog/calculators/ROASCalculator.tsx

'use client'

import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import Link from 'next/link'
import { cn, formatCurrency } from '~/lib/utils'
import RiShoppingBag2Line from '~/icons/remix/generated/RiShoppingBag2Line'
import RiBuilding2Line from '~/icons/remix/generated/RiBuilding2Line'
import { FAQ } from '~/components/shared/FAQ'
import { BenchmarkComparison } from '~/components/resources/benchmarks/BenchmarkComparison'
import { ROAS_BENCHMARK, findSegmentById } from '~/lib/resources/benchmarks'
import { EmbeddedEmailCapture } from '~/components/forms/variants/embedded/EmbeddedEmailCapture'
import type { EmbeddedEmailCaptureProps } from '~/components/forms/variants/embedded/types'
import { sendResourceEvent, createEngagementTimers } from '~/lib/gtag'
import { debounce } from '~/lib/utils/events'
import {
  calculateMetrics,
  type BusinessType,
  type CalculatorInputs,
  type CalculatorResults,
  type ROASCalculatorMode,
} from './ROASCalculator.logic'

// Related-link entries live in a sibling `.related.ts` (no `'use client'`)
// so the slugs can be unit-tested against the live resource registries
// — see `src/lib/resources/__tests__/related-link-slugs.test.ts`.
// A typo or registry rename now fails CI instead of silently shipping
// a 404 link.
import { RELATED_GLOSSARY, RELATED_TOOLS } from './ROASCalculator.related'

interface EmailTemplateConfig {
  templateAlias: string
  to: string
  from: string
  templateModel: {
    base: {
      name: string
      logo_url: string
      product_name: string
      product_url: string
      support_email: string
      sender_name: string
      company_name: string
      company_address: string
      email: string
    }
    custom: {
      calculator_type: string
      calculator_results: {
        metrics: CalculatorResults
        inputs: {
          adSpend: number
          cpc: number
          conversionRate: number
          averageValue: number
          leadToCustomerRate?: number
        }
      } | null
    }
  }
}

const FAQ_ITEMS = [
  {
    question: "What is ROAS?",
    answer: "Return on Ad Spend (ROAS) is a marketing metric that measures the revenue generated for every dollar spent on advertising. A ROAS of 3.0 means you earn $3 for every $1 spent on ads. It's a fundamental KPI for evaluating advertising efficiency across channels like Google Ads, Facebook, or other digital platforms."
  },
  {
    question: "What's a 'good' ROAS?",
    answer: "A good ROAS varies by industry and business model. Generally, a ROAS of 3.0 or higher is considered good for eCommerce, while B2B companies might accept lower ROAS due to higher lifetime customer values. Your target ROAS should also account for profit margins, overhead costs, and business growth objectives."
  },
  {
    question: "How is ROAS calculated?",
    answer: "ROAS is calculated by dividing your total revenue from advertising by your total advertising spend. The formula is: ROAS = Revenue / Ad Spend. For example, if you spent $1,000 on ads and generated $4,000 in revenue, your ROAS would be 4:1 (or 4.0). For B2B companies, the calculation may need to account for lead-to-customer conversion rates and sales cycle length."
  },
  {
    question: "What's the difference between ROAS and ROI?",
    answer: "ROAS measures revenue generated per ad dollar spent, while ROI (Return on Investment) takes into account all costs (not just ad spend) and measures profit rather than revenue. The ROI formula is: ROI = (Net Profit / Total Investment) × 100%. ROAS focuses specifically on advertising effectiveness, while ROI provides a broader view of overall profitability."
  },
  {
    question: "How does ROAS differ for B2B vs. eCommerce?",
    answer: "For eCommerce, ROAS calculation is straightforward as conversions directly result in revenue. For B2B, the calculation is more complex due to longer sales cycles and the lead-to-customer journey. B2B companies often need to factor in lead quality, sales cycle length, and customer lifetime value. While eCommerce might focus on immediate ROAS, B2B companies may accept a lower initial ROAS knowing the long-term customer value will provide greater returns."
  },
  {
    question: "Should I include customer lifetime value in ROAS calculations?",
    answer: "Including customer lifetime value (CLV) in your ROAS calculations can provide a more comprehensive view of advertising effectiveness, especially for subscription businesses or companies with high repeat purchase rates. This is sometimes called LTV:CAC (Lifetime Value to Customer Acquisition Cost) ratio. While traditional ROAS looks at immediate return, CLV-adjusted ROAS helps you understand the long-term impact of your advertising investments."
  },
  {
    question: "ROAS vs. MER — which should I use?",
    answer: "Use both, for different decisions. ROAS (attributed revenue ÷ ad spend) is best for in-channel optimization — which campaigns to scale, which creatives to cut — but it's inflated by attribution overlap, since every platform claims the same conversion. Marketing Efficiency Ratio (MER = total revenue ÷ total marketing spend) is best for budget and whole-program decisions. A common failure mode is scaling on platform ROAS while blended MER quietly goes sideways on duplicated attribution. Track ROAS for tactics and MER for strategy."
  }
]

/**
 * Renders a metric value, or '—' when it is non-finite. Clearing/zeroing an
 * input (ad spend, CPC, conversion rate) produces a divide-by-zero, so a metric
 * can be Infinity/NaN; show an em dash instead of 'Infinityx' / '$NaN' / '∞'.
 */
function metric(value: number, render: (v: number) => string): string {
  return Number.isFinite(value) ? render(value) : '—'
}

/**
 * Props for the ROAS calculator. All optional so the umbrella
 * `roas-calculator` page can render it config-free, while SEO landing
 * variants (`target-roas-calculator`, `b2b-roas-calculator`, …) drive
 * the heading, FAQ, and initial business type from their own data
 * record. The route passes `faqItems` straight from the calculator's
 * registry `faqs`, so the VISIBLE FAQ and the `FAQPage` JSON-LD share a
 * single source (the registry) and can never drift apart.
 */
export interface ROASCalculatorProps {
  /** In-tool H2. Defaults to the umbrella's "ROAS Calculator". */
  heading?: string
  /** Sub-heading under the H2. */
  subheading?: string
  /** FAQ entries to render. Passed from the registry record so visible
   *  copy matches the JSON-LD; when empty the FAQ block is omitted. */
  faqItems?: { question: string; answer: string }[]
  /** Pre-selected business type (a `b2b-roas-calculator` variant boots
   *  on the B2B tab; the umbrella defaults to eCommerce). */
  initialBusinessType?: BusinessType
  /** Calculation the tool performs. `'roi'` adds a margin input + Marketing
   *  ROI; `'target'` adds margin + target-margin inputs and break-even /
   *  target ROAS. Defaults to `'roas'` (umbrella + eCommerce/B2B variants),
   *  which is byte-identical to the historical behavior. */
  mode?: ROASCalculatorMode
  /** Slug used for analytics/CRM so each variant reports its own
   *  CTR/conversion signal instead of folding into the umbrella. */
  trackingSlug?: string
}

export function ROASCalculator({
  heading = 'ROAS Calculator',
  subheading = 'Calculate your Return on Ad Spend and key performance metrics',
  faqItems = FAQ_ITEMS,
  initialBusinessType = 'ecommerce',
  mode = 'roas',
  trackingSlug = 'roas-calculator',
}: ROASCalculatorProps = {}): JSX.Element {
  const [inputs, setInputs] = useState<CalculatorInputs>({
    businessType: initialBusinessType,
    adSpend: 1000,
    cpc: 1.00,
    conversionRate: 2.00,
    // Default AOV gives a realistic ~3.0x ROAS out of the box (the DTC
    // e-commerce median), so the benchmark card demonstrates a healthy,
    // mid-range result on first load rather than a 1.0x "well below" edge case.
    averageValue: 150.00,
    leadToCustomerRate: 20.00,
    // Margins drive the ROI/target-ROAS modes and the umbrella's optional
    // break-even readout. The variants default to 45% (with a 20% target net
    // margin that's a tidy demo: break-even ≈ 2.2x, target ≈ 4.0x); the
    // umbrella 'roas' mode defaults to 0 so margin math stays strictly
    // opt-in and first-load behavior is unchanged.
    grossMargin: mode === 'roas' ? 0 : 45.00,
    targetProfitMargin: 20.00
  })

  const [results, setResults] = useState<CalculatorResults | null>(null)
  const [showEmailCapture, setShowEmailCapture] = useState(false)
  const [email] = useState('')
  const [getTotalMs, getDeltaMs] = useMemo(() => createEngagementTimers(), [])

  const handleCalculate = (): void => {
    const newResults = calculateMetrics(inputs, mode)
    setResults(newResults)
    setShowEmailCapture(true)
    // Track interaction (delta) and completion (total) when results are produced
    sendResourceEvent('interact', {
      resourceType: 'calculator',
      resourceSlug: trackingSlug,
      engagementMs: getDeltaMs(),
      params: { interaction_type: 'calculate' }
    })
    sendResourceEvent('complete', {
      resourceType: 'calculator',
      resourceSlug: trackingSlug,
      engagementMs: getTotalMs(),
      params: { roas: newResults.roas, revenue: newResults.revenue }
    })
  }

  // Track input interactions without sending values (lean payload) with debounce
  const _debouncedInteract = useMemo(() => debounce((...args: unknown[]) => {
    const interactionType = (args[0] as string) ?? 'input_change'
    sendResourceEvent('interact', {
      resourceType: 'calculator',
      resourceSlug: trackingSlug,
      engagementMs: getDeltaMs(),
      params: { interaction_type: interactionType }
    })
  }, 400), [getDeltaMs, trackingSlug])

  const trackInteract = (interactionType: string): void => {
    _debouncedInteract(interactionType)
  }

  const emailTemplate = useMemo(() => {
    // Base email template configuration
    const baseTemplate: EmailTemplateConfig = {
      templateAlias: 'welcome',
      to: '',
      from: process.env.NEXT_PUBLIC_EMAIL_FROM ?? 'info@adsights.ai',
      templateModel: {
        base: {
          name: email ? email.split('@')[0]?.trim() ?? '' : '', // Set name from email and handle undefined
          logo_url: 'https://www.adsights.ai/logo-square.png',
          product_name: 'AdSights',
          product_url: 'https://www.adsights.ai',
          support_email: 'support@adsights.ai',
          sender_name: 'The AdSights Team',
          company_name: 'AdSights Inc.',
          company_address: '254 Chapman Rd, Ste 208 #28056, Newark, Delaware 19702',
          email: email ?? '' // Add email to template data with fallback
        },
        custom: {
          calculator_type: 'ROAS',
          // Build the payload from the input SNAPSHOT captured in `results`, not
          // the live form — editing an input after calculating (but before
          // submitting the email) must not pair these metrics with mismatched
          // inputs.
          calculator_results: results ? {
            metrics: results,
            inputs: {
              adSpend: results.inputs.adSpend,
              cpc: results.inputs.cpc,
              conversionRate: results.inputs.conversionRate,
              averageValue: results.inputs.averageValue,
              ...(results.inputs.leadToCustomerRate && { leadToCustomerRate: results.inputs.leadToCustomerRate })
            }
          } : null
        }
      }
    }

    return baseTemplate
  }, [email, results])

  const emailCaptureConfig: EmbeddedEmailCaptureProps = {
    preset: 'indigo',
    content: {
      title: {
        text: "Get Your ROAS Analysis"
      },
      description: {
        text: "We'll send you a detailed breakdown of your metrics and personalized recommendations."
      },
      button: {
        text: "Get Analysis",
        loadingText: "Preparing Analysis...",
        icon: null,
        className: "flex w-auto items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
      },
      input: {
        label: "Work Email",
        placeholder: "Enter your work email"
      },
      messages: {
        success: "Analysis sent!",
        error: "Please enter a valid email address."
      }
    },
    template: emailTemplate,
    analytics: {
      enabled: true,
      category: 'calculators',
      action: 'email_capture',
      label: `${trackingSlug.replace(/-/g, '_')}_results`,
      elementId: `${trackingSlug}-email-form`,
      pageLocation: heading
    },
    crm: {
      tags: ['calculator_results', 'roas'],
      source: trackingSlug.replace(/-/g, '_'),
      // Read from the result snapshot (`results.inputs`), never live `inputs`,
      // so an edit after Calculate can't store a mismatched input/metric pair.
      customFields: {
        calculator_type: 'ROAS',
        calculator_business_type: results?.inputs.businessType,
        calculator_ad_spend: results?.inputs.adSpend,
        calculator_roas: results?.roas
      }
    }
  }

  return (
    <div className="mx-auto max-w-4xl" role="main" aria-labelledby="calculator-heading">
      <div className="rounded-2xl bg-white shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
        {/* Calculator Header */}
        <div className="px-8 pt-8">
          <h2 id="calculator-heading" className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            {heading}
          </h2>
          <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
            {subheading}
          </p>
        </div>

        {/* Business Type Selection */}
        <div className="mt-8 px-8" role="radiogroup" aria-labelledby="business-type-label">
          <label id="business-type-label" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Business Type
          </label>
          <div className="mt-2 grid grid-cols-2 gap-4">
            <button
              onClick={() => setInputs({ ...inputs, businessType: 'ecommerce' })}
              aria-pressed={inputs.businessType === 'ecommerce'}
              className={cn(
                "flex items-center justify-center gap-2 rounded-lg border p-4 text-sm font-medium",
                inputs.businessType === 'ecommerce'
                  ? "border-indigo-500 bg-indigo-50 text-indigo-600 dark:border-indigo-400 dark:bg-indigo-900/20 dark:text-indigo-400"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
              )}
            >
              <RiShoppingBag2Line className="h-5 w-5" aria-hidden="true" />
              eCommerce
            </button>
            <button
              onClick={() => setInputs({ ...inputs, businessType: 'b2b' })}
              aria-pressed={inputs.businessType === 'b2b'}
              className={cn(
                "flex items-center justify-center gap-2 rounded-lg border p-4 text-sm font-medium",
                inputs.businessType === 'b2b'
                  ? "border-indigo-500 bg-indigo-50 text-indigo-600 dark:border-indigo-400 dark:bg-indigo-900/20 dark:text-indigo-400"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
              )}
            >
              <RiBuilding2Line className="h-5 w-5" aria-hidden="true" />
              B2B / Lead Gen
            </button>
          </div>
        </div>

        {/* Calculator Form */}
        <div className="mt-8 grid gap-8 px-8 pb-8 sm:grid-cols-2">
          {/* Left Column */}
          <div className="space-y-6">
            <div>
              <label htmlFor="ad-spend" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Monthly Ad Spend ($)
              </label>
              <input
                id="ad-spend"
                type="number"
                value={inputs.adSpend}
                onChange={(e) => { setInputs({ ...inputs, adSpend: +e.target.value }); trackInteract('input_change') }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
                aria-describedby="ad-spend-description"
              />
            </div>

            <div>
              <label htmlFor="cpc" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Average Cost per Click ($)
              </label>
              <input
                id="cpc"
                type="number"
                step="0.01"
                value={inputs.cpc.toFixed(2)}
                onChange={(e) => { setInputs({ ...inputs, cpc: +e.target.value }); trackInteract('input_change') }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
              />
            </div>

            <div>
              <label htmlFor="conversion-rate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Conversion Rate (%)
              </label>
              <input
                id="conversion-rate"
                type="number"
                step="0.01"
                value={inputs.conversionRate.toFixed(2)}
                onChange={(e) => { setInputs({ ...inputs, conversionRate: +e.target.value }); trackInteract('input_change') }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
                aria-describedby="conversion-rate-description"
              />
              <p id="conversion-rate-description" className="mt-1 text-sm text-gray-500">
                {inputs.businessType === 'ecommerce' 
                  ? 'Percentage of visitors who make a purchase'
                  : 'Percentage of visitors who become leads'}
              </p>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            <div>
              <label htmlFor="average-value" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {inputs.businessType === 'ecommerce' 
                  ? 'Average Order Value ($)'
                  : 'Average Customer Value ($)'}
              </label>
              <input
                id="average-value"
                type="number"
                step="0.01"
                value={inputs.averageValue.toFixed(2)}
                onChange={(e) => { setInputs({ ...inputs, averageValue: +e.target.value }); trackInteract('input_change') }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
              />
            </div>

            {inputs.businessType === 'b2b' && (
              <div>
                <label htmlFor="lead-rate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Lead to Customer Rate (%)
                </label>
                <input
                  id="lead-rate"
                  type="number"
                  step="0.01"
                  value={inputs.leadToCustomerRate?.toFixed(2)}
                  onChange={(e) => { setInputs({ ...inputs, leadToCustomerRate: +e.target.value }); trackInteract('input_change') }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
                  aria-describedby="lead-rate-description"
                />
                <p id="lead-rate-description" className="mt-1 text-sm text-gray-500">
                  Percentage of leads that convert to customers
                </p>
              </div>
            )}

            {/* Gross margin — required by the ROI / target-ROAS modes, and
                optional on the umbrella 'roas' mode, where a non-zero margin
                surfaces break-even ROAS so the page's "compare your result to
                break-even, not headline averages" guidance is computable in
                place rather than locked behind the target/ROI variants. */}
            <div>
              <label htmlFor="gross-margin" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Gross Profit Margin (%){mode === 'roas' ? ' — optional' : ''}
              </label>
              <input
                id="gross-margin"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={inputs.grossMargin.toFixed(2)}
                onChange={(e) => { setInputs({ ...inputs, grossMargin: +e.target.value }); trackInteract('input_change') }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
                aria-describedby="gross-margin-description"
              />
              <p id="gross-margin-description" className="mt-1 text-sm text-gray-500">
                {mode === 'roas'
                  ? 'Optional — enter your margin to compare realized ROAS against break-even (1 ÷ margin). Leave at 0 to skip.'
                  : 'Share of revenue left after cost of goods — used for true profit, ROI, and break-even ROAS'}
              </p>
            </div>

            {mode === 'target' && (
              <div>
                <label htmlFor="target-margin" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Target Profit Margin (%)
                </label>
                <input
                  id="target-margin"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={inputs.targetProfitMargin.toFixed(2)}
                  onChange={(e) => { setInputs({ ...inputs, targetProfitMargin: +e.target.value }); trackInteract('input_change') }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
                  aria-describedby="target-margin-description"
                />
                <p id="target-margin-description" className="mt-1 text-sm text-gray-500">
                  Net margin you want left after COGS and ad spend (0% = break-even). Must be below your gross margin.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Calculate Button */}
        <div className="border-t border-gray-200 bg-gray-50 px-8 py-6 dark:border-gray-800 dark:bg-gray-900/50 rounded-b-2xl">
          <button
            onClick={handleCalculate}
            className="w-full rounded-lg bg-indigo-600 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            aria-label={mode === 'roi' ? 'Calculate ROI and ROAS' : mode === 'target' ? 'Calculate target ROAS' : 'Calculate ROAS metrics'}
          >
            {mode === 'roi' ? 'Calculate ROI' : mode === 'target' ? 'Calculate Target ROAS' : 'Calculate ROAS'}
          </button>
        </div>
      </div>

      {/* Results Section */}
      <AnimatePresence>
        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mt-8 space-y-8 rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800"
            role="region"
            aria-label="Calculation Results"
          >
            {/* Primary Metrics */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Key Performance Metrics
              </h3>
              <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">ROAS</dt>
                  <dd className="mt-2 text-2xl font-semibold text-indigo-600 dark:text-indigo-400 truncate">
                    {metric(results.roas, (v) => `${v.toFixed(2)}x`)}
                  </dd>
                </div>
                {/* Marketing ROI — the headline number on the ad-spend-ROI
                    variant. Only present once a gross margin is known. */}
                {results.roi !== undefined && (
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Marketing ROI</dt>
                    <dd className="mt-2 text-2xl font-semibold text-indigo-600 dark:text-indigo-400 truncate">
                      {metric(results.roi, (v) => `${v.toFixed(0)}%`)}
                    </dd>
                  </div>
                )}
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Revenue</dt>
                  <dd className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white truncate">
                    {metric(results.revenue, (v) => formatCurrency(v, 2))}
                  </dd>
                </div>
                {/* Profit becomes COGS-aware ("Net Profit") once a gross
                    margin is supplied, so it matches the ROI the page promises
                    instead of contradicting it. */}
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {results.netProfit !== undefined ? 'Net Profit' : 'Profit'}
                  </dt>
                  <dd className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white truncate">
                    {metric(results.netProfit ?? results.profit, (v) => formatCurrency(v, 2))}
                  </dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {inputs.businessType === 'ecommerce' ? 'Cost per Sale' : 'Cost per Lead'}
                  </dt>
                  <dd className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white truncate">
                    {metric(results.cpa, (v) => formatCurrency(v, 2))}
                  </dd>
                </div>
              </dl>
              {results.netProfit !== undefined && (
                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                  {results.mode === 'roas'
                    ? 'Net Profit accounts for your cost of goods (via the optional gross margin), not just ad spend.'
                    : 'Net Profit and Marketing ROI account for your cost of goods (via gross margin), not just ad spend — ROI = net profit ÷ ad spend.'}
                </p>
              )}
            </div>

            {/* Required-ROAS block — target mode, plus the umbrella 'roas'
                mode when the optional margin produced a break-even number.
                Turns the page's "break-even ROAS = 1 ÷ margin" lesson into an
                actual computed number the realized ROAS above can be judged
                against. (The 'roi' mode reports ROI instead.) */}
            {results.breakEvenRoas !== undefined && results.mode !== 'roi' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {results.mode === 'target'
                    ? 'ROAS Your Margins Require'
                    : 'Break-even ROAS at Your Margin'}
                </h3>
                <dl className={`mt-4 grid grid-cols-1 gap-4 ${results.mode === 'target' ? 'sm:grid-cols-2' : ''}`}>
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Break-even ROAS</dt>
                    <dd className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white truncate">
                      {metric(results.breakEvenRoas ?? NaN, (v) => `${v.toFixed(2)}x`)}
                    </dd>
                  </div>
                  {results.mode === 'target' && (
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Target ROAS ({(results.targetProfitMargin ?? 0).toFixed(0)}% margin)
                      </dt>
                      <dd className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white truncate">
                        {metric(results.targetRoas ?? NaN, (v) => `${v.toFixed(2)}x`)}
                      </dd>
                    </div>
                  )}
                </dl>
                {/* Compare realized vs. break-even so the number is actionable. */}
                {Number.isFinite(results.roas) && Number.isFinite(results.breakEvenRoas) && (
                  <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                    {results.roas >= (results.breakEvenRoas ?? Infinity)
                      ? `Your realized ${results.roas.toFixed(2)}x ROAS clears the ${(results.breakEvenRoas ?? 0).toFixed(2)}x break-even — the campaign is profitable at this margin.`
                      : `Your realized ${results.roas.toFixed(2)}x ROAS is below the ${(results.breakEvenRoas ?? 0).toFixed(2)}x break-even — the campaign loses money at this margin until ROAS or margin improves.`}
                    {results.mode === 'target' &&
                      (Number.isFinite(results.targetRoas)
                        ? ` Reaching a ${(results.targetProfitMargin ?? 0).toFixed(0)}% net margin needs ${(results.targetRoas ?? 0).toFixed(2)}x.`
                        : ' A target margin at or above your gross margin is unreachable at any ROAS — lower it below the gross margin.')}
                  </p>
                )}
              </div>
            )}

            {/* Industry benchmark comparison — grounds the ROAS result against
                cited segment benchmarks (the HubSpot/Triple Whale "compare to
                industry average" mechanic; see the off-page strategy Pillar 3).
                Segment follows the live businessType toggle, matching the
                existing Cost-per-Sale/Lead labelling above. */}
            {(() => {
              // Use the business type captured WITH this result (not the live
              // toggle) and look up by stable id, so the benchmark segment always
              // matches the displayed ROAS and can't break if the segment's
              // display copy is later edited in data.ts.
              const segment = findSegmentById(
                ROAS_BENCHMARK,
                results.businessType === 'b2b' ? 'b2b-saas' : 'dtc-ecommerce',
              )
              return segment ? (
                <BenchmarkComparison set={ROAS_BENCHMARK} segment={segment} value={results.roas} />
              ) : null
            })()}

            {/* Secondary Metrics */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Campaign Performance
              </h3>
              <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Clicks</dt>
                  <dd className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                    {metric(results.clicks, (v) => v.toLocaleString())}
                  </dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {inputs.businessType === 'ecommerce' ? 'Sales' : 'Leads'}
                  </dt>
                  <dd className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                    {metric(results.conversions, (v) => v.toLocaleString())}
                  </dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {inputs.businessType === 'ecommerce' ? 'Value per Sale' : 'Value per Lead'}
                  </dt>
                  <dd className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                    {metric(results.leadValue, (v) => formatCurrency(v, 2))}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Email Capture */}
            {showEmailCapture && results && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="rounded-lg bg-indigo-50 dark:bg-indigo-900/20"
              >
                <EmbeddedEmailCapture {...emailCaptureConfig} />
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Related concepts — hardcoded SSR-visible cross-links.
          See the RELATED_GLOSSARY / RELATED_TOOLS constants at the
          top of this file for the rationale on why these are static
          links rather than fetched via CalculatorLayout's client-side
          RelatedContent block. */}
      <section
        aria-labelledby="roas-related-concepts"
        className="mt-12 rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800"
      >
        <h3
          id="roas-related-concepts"
          className="text-2xl font-semibold text-gray-900 dark:text-white mb-3"
        >
          Related concepts &amp; calculators
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          ROAS sits inside a larger paid-media efficiency framework. Use these
          definitions and adjacent calculators to interpret your result in
          context — channel benchmarks, attribution caveats, LTV-adjustment,
          and the testing cadence that actually moves ROAS over time.
        </p>
        <div className="grid gap-8 md:grid-cols-2">
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
              Glossary terms
            </h4>
            <ul className="space-y-3">
              {RELATED_GLOSSARY.map((item) => (
                <li key={item.slug} className="text-sm">
                  <Link
                    href={`/resources/glossary/${item.slug}`}
                    className="font-medium text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {item.label}
                  </Link>
                  <span className="text-gray-600 dark:text-gray-400"> — {item.note}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
              Other calculators
            </h4>
            <ul className="space-y-3">
              {RELATED_TOOLS.map((item) => (
                <li key={item.slug} className="text-sm">
                  <Link
                    href={`/resources/tools/calculators/${item.slug}`}
                    className="font-medium text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {item.label}
                  </Link>
                  <span className="text-gray-600 dark:text-gray-400"> — {item.note}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ Section — `faqItems` comes from the calculator's registry
          record (passed by the route), the same source the `FAQPage`
          JSON-LD is generated from, so visible copy and structured data
          stay in lockstep. Omitted entirely when a record carries no
          FAQs. */}
      {faqItems.length > 0 && (
        <div className="mt-12 rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
          <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
            Frequently Asked Questions
          </h3>
          <FAQ
            items={faqItems}
            type="single"
            disableSchema={true}
            features={{ showTitle: false }}
            theme={{ size: "xl" }}
          />
        </div>
      )}
    </div>
  )
} 